import React, { useState, useCallback, useEffect } from "react";
import HomeIcon from "@mui/icons-material/Home";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadTable from "./widgets/downloaddata";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import "jspdf-autotable";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import {
  GetDepositlist,
  setFilters,
  setPage,
  setRowsPerPage,
} from "../redux/slices/depositlist";
import moment from "moment";
import { getUserDataDeposits, RightOffCash } from "../redux/slices/offcanvas";
import { tabsSuccess } from "../redux/slices/tabs";
import { keyframes } from "@emotion/react";
import DownloadData from "./widgets/downloaddata";
import DoneIcon from "@mui/icons-material/Done";
import CancelIcon from "@mui/icons-material/Cancel";
import LazyTable from "./widgets/lazytable";

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const AddCredit = (read) => {
  const dispatch = useDispatch();
  const { page, data, rowsPerPage, totalDeposits, loading, filters } =
    useSelector((v) => v.deposits);
  const user = useSelector((v) => v.login.user);
  const [imageUrl, setImageUrl] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [zoom, setZoom] = useState(1);

  const fetchDepositList = useCallback(() => {
    dispatch(GetDepositlist(page, rowsPerPage, filters));
  }, [dispatch, page, rowsPerPage, filters]);

  useEffect(() => {
    fetchDepositList();
  }, [page, rowsPerPage, filters]);

  const [selectedFormat, setSelectedFormat] = useState("pdf");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleChangePage = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(event.target.value, 10));
    dispatch(setPage(0));
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied: " + text);
  };

  const handleFormatChange = (event) => {
    setSelectedFormat(event.target.value);
  };

  const statusBodyTemplate = (rowData) => {
    let color, icon, statusText;
    if (rowData.status === "paid") {
      color = "#4CAF50";
      statusText = "Approved";

      icon = (
        <>
          <DoneIcon sx={{ color: "white", fontSize: "16px" }} />
        </>
      );
    } else if (rowData.status === "rejected") {
      color = "red";
      statusText = "Rejected";
      icon = (
        <>
          <CancelIcon sx={{ color: "white", fontSize: "16px" }} />
        </>
      );
    } else if (rowData.status === "deposit") {
      color = "#4CAF50";
      statusText = "Approved";

      icon = (
        <>
          <DoneIcon sx={{ color: "white", fontSize: "16px" }} />
        </>
      );
    } else {
      color = "#FFC104";
      statusText = "Pending";
      icon = (
        <>
          <DoneIcon sx={{ color: "white", fontSize: "16px" }} />
        </>
      );
    }

    return (
      <div className="flex justify-start items-center">
        <Box
          className="mr-2"
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}{" "}
        </Box>
        {rowData.status === "paid"
          ? "Done"
          : rowData.status === "deposit"
          ? "Done"
          : rowData.status === "rejected"
          ? "Rejected"
          : "Pending"}
      </div>
    );
  };

  const transitionIdBodyTemplate = (rowData) => {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        {rowData.transitionId}
        <IconButton
          fontSize="small"
          onClick={() => handleCopy(rowData.transitionId)}
          sx={{ padding: 0 }}
        >
          <ContentCopyOutlinedIcon fontSize="inherit" sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  };

  const actionBodyTemplate = (rowData) => {
    if (read?.read?.access === "read") {
      return <></>;
    } else if (rowData?.status == "paid") {
      return "";
    } else if (rowData?.status == "deposit") {
      return "";
    } else if (rowData?.status == "rejected") {
      if (
        user?.role === "admin" ||
        user?.role === "superadmin" ||
        user?.role === "manager"
      ) {
        return (
          <Box display="flex" gap={1}>
            <>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleOpen(rowData, "approve")}
              >
                Approve
              </Button>
            </>
          </Box>
        );
      }
    } else if (rowData?.status == "pending") {
      return (
        <Box display="flex" gap={1}>
          <>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleOpen(rowData, "approve")}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => handleOpen(rowData, "rejected")}
            >
              Reject
            </Button>
          </>
        </Box>
      );
    }
  };

  const handleOpen = async (datas, paths) => {
    if (read === "read") {
      return null;
    }
    const params = Object.assign({}, datas, { action: "deposit" });
    await dispatch(RightOffCash(params));

    const payload = {
      uid: params?.userid,
      cid: params?._id,
    };
    if (paths === "approve") {
      dispatch(tabsSuccess("approve"));
    } else if (paths === "rejected") {
      dispatch(tabsSuccess("rejected"));
    }

    await dispatch(getUserDataDeposits(payload));
  };

  const headers = [
    "#",
    "Customer Name",
    "Before Balance",
    "Deposit Amount",
    "After Balance",
    "Date",
    "Status",
    "Transaction ID",
  ];

  const tableData = data?.map((row, index) => [
    index + 1,
    row.username,
    `$${parseInt(row.balance) ? parseInt(row.balance) : 0}`,
    `$${parseInt(row.amount) ? parseInt(row.amount) : 0}`,
    `$${
      row.status === "deposit"
        ? parseInt(row?.amount) > 0
          ? parseInt(row.balance) + parseInt(row?.amount)
          : 0
        : parseInt(row?.balance)
    }`,
    moment(row.createdAt).format("DD/MM/YY hh:mm A"),
    row.status,
    row.transid || "N/A",
  ]);
  const handleViewImage = (rowData) => {
    if (rowData?.attachments) {
      setImageUrl(rowData?.attachments);
      setOpenImageDialog(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          my: 4,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box>
            <Typography variant="h5">
              Deposit Requests
              <IconButton
                onClick={fetchDepositList}
                disabled={loading}
                sx={{
                  backgroundColor: "#3f51b5",
                  ml: 2,
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#303f9f",
                  },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                  },
                  animation: loading ? `${rotate} 1s linear infinite` : "none",
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Typography>
          </Box>
        </Box>
        <DownloadData
          headers={headers || []}
          tableData={tableData || []}
          fileName={"Deposits-requests"}
        />
      </Box>
      {/* <Box>
        <ButtonTabs />
      </Box> */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        message="Please select an export format."
        onClose={() => setOpenSnackbar(false)}
      />
      <LazyTable
        data={data}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={totalDeposits}
        loading={loading}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onFilterChange={(filters) => {
          dispatch(setFilters(filters));
        }}
        columns={[
          { field: "username", headerName: "Customers", filter: true },
          {
            field: "balance",
            headerName: "Before Balance",
            filter: true,
            render: (params) =>
              `$${parseInt(params.balance) ? parseInt(params.balance) : 0}`,
          },
          {
            field: "amount",
            headerName: "Deposit Amount",
            filter: true,
            render: (params) =>
              `$${parseInt(params.amount) ? parseInt(params.amount) : 0}`,
          },
          {
            field: "balance",
            headerName: "After Balane",
            filter: true,
            render: (params) =>
              `$${
                params.status === "paid" || params.status === "deposit"
                  ? parseInt(params?.amount) > 0
                    ? parseInt(params.balance) + parseInt(params?.amount)
                    : 0
                  : params.status === "rejected"
                  ? parseInt(params.balance)
                  : parseInt(params.balance) + parseInt(params?.amount)
              }`,
          },
          {
            field: "cam",
            headerName: "Payment Method",
            filter: true,
            render: (rowData) =>
              rowData?.cam && (
                <span className="capitalize">{rowData?.cam}</span>
              ),
          },
          {
            field: "createdAt",
            headerName: "Date",
            filter: true,
            render: (rowData) => moment(rowData?.createdAt).format("L LT"),
          },
          {
            field: "active",
            headerName: "Status",
            filter: true,
            render: statusBodyTemplate,
          },
          {
            field: "attachment",
            headerName: "Attachment",
            render: (rowData) =>
              rowData.attachments?.length ? (
                <Button
                  variant="text"
                  onClick={() => handleViewImage(rowData)}
                  sx={{ textDecoration: "underline", padding: 0 }}
                >
                  View
                </Button>
              ) : (
                <span>Not Available</span>
              ),
          },
          read?.read?.access != "read" && {
            field: "actions",
            headerName: "Actions",
            render: actionBodyTemplate,
          },
        ]}
      />
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>View Attachment</DialogTitle>
        <DialogContent>
          {imageUrl && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
                overflow: "hidden",
              }}
            >
              {imageUrl?.map((img, index) => (
                <img
                  src={img.image}
                  alt="Attachment"
                  style={{
                    transform: `scale(${zoom})`,
                    transition: "transform 0.3s ease-in-out",
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZoom(zoom - 0.1)} disabled={zoom <= 0.5}>
            Zoom Out
          </Button>
          <Button onClick={() => setZoom(zoom + 0.1)} disabled={zoom >= 2}>
            Zoom In
          </Button>
          <Button onClick={() => setOpenImageDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddCredit;
